// Pure types + aggregation for the Sharing tab. Rows come from the Supabase
// views sharing_referrers / sharing_redemptions (supabase/schemas/03_views.sql,
// fed by the WhatsApp bot's GIFT- codes); the math lives here so it is
// unit-testable without rendering.

import type { EchoflicksVideoSession } from "../types";

export type SharingReferrerRow = {
  id: string;
  code: string;
  owner_wa_id: string;
  created_at: string;
  owner_contact_id: number | string | null;
  owner_name: string | null;
  redemptions: number;
  new_users: number;
  last_redeemed_at: string | null;
  // Reward mirror (bot referral_rewards ledger): optional so rows served by
  // the pre-reward view shape still type-check.
  credits_earned?: number;
  credits_pending?: number;
  // Redemptions claimed through a my.echoflicks.com invite link (migration
  // 20261013200000_gift_redemption_channel). Optional for the same reason.
  web_redemptions?: number;
};

// Where the friend joined: "whatsapp" = their first message to Felix carried
// the code; "web" = they claimed a my.echoflicks.com invite link.
export type ReferralChannel = "whatsapp" | "web";

export type RewardStatus =
  | "pending"
  | "qualified"
  | "granted"
  | "skipped"
  | "legacy";

export type SharingRedemptionRow = {
  id: string;
  code: string;
  gift_code_id: string;
  referrer_wa_id: string;
  referred_wa_id: string;
  referred_is_new: boolean;
  redeemed_at: string;
  referrer_contact_id: number | string | null;
  referrer_name: string | null;
  referred_contact_id: number | string | null;
  referred_name: string | null;
  reward_status?: RewardStatus | string | null;
  reward_skip_reason?: string | null;
  qualifying_workload_id?: string | null;
  reward_granted_at?: string | null;
  reward_grant_id?: string | null;
  reward_notified_at?: string | null;
  // Optional: rows served by the pre-channel view shape have no channel and
  // are all WhatsApp redemptions (the column defaults to 'whatsapp').
  channel?: ReferralChannel;
};

export type SharingTotals = {
  codesIssued: number;
  totalRedemptions: number;
  newUsers: number;
  activeReferrers: number;
  creditsEarned: number;
  creditsPending: number;
  webRedemptions: number;
};

// A missing or unrecognized channel reads as WhatsApp: every redemption
// recorded before web referrals existed came from a WhatsApp first message.
export function redemptionChannel(
  row: Pick<SharingRedemptionRow, "channel">,
): ReferralChannel {
  return row.channel === "web" ? "web" : "whatsapp";
}

export function channelLabel(channel: ReferralChannel): string {
  return channel === "web" ? "Web" : "WhatsApp";
}

export type RewardChip = {
  label: string;
  tone: "earned" | "waiting" | "skipped" | "muted";
};

const SKIP_REASON_LABELS: Record<string, string> = {
  existing_user: "Existing user",
  policy_disabled: "Rewards off",
  cap_exceeded: "Cap reached",
  referrer_stopped: "Referrer opted out",
  not_new_user: "Not a new user",
  referrer_missing: "Referrer unknown",
  self_referral: "Self-referral",
};

// What the redemption feed shows in its "Credit" column. Older rows (before
// rewards existed) and rows from the pre-reward view shape read as muted.
export function rewardChip(
  row: Pick<
    SharingRedemptionRow,
    "reward_status" | "reward_skip_reason" | "reward_notified_at"
  >,
): RewardChip {
  switch (row.reward_status) {
    case "granted":
      return {
        label: row.reward_notified_at
          ? "Credit earned · notified"
          : "Credit earned",
        tone: "earned",
      };
    case "qualified":
      return { label: "Qualified · credit pending", tone: "waiting" };
    case "pending":
      return { label: "Credit pending", tone: "waiting" };
    case "skipped":
      return {
        label: `No credit · ${SKIP_REASON_LABELS[row.reward_skip_reason ?? ""] ?? row.reward_skip_reason ?? "skipped"}`,
        tone: "skipped",
      };
    case "legacy":
      return { label: "Before rewards", tone: "muted" };
    default:
      return { label: "—", tone: "muted" };
  }
}

export type ReferralRelationship = SharingRedemptionRow & {
  echoes: EchoflicksVideoSession[];
  latestEcho: EchoflicksVideoSession | null;
};

export type ReferrerPerformance = SharingReferrerRow & {
  echoMakers: number;
  referralEchoes: number;
  echoConversionRate: number;
};

export type ReferralAnalytics = {
  relationships: ReferralRelationship[];
  referrers: ReferrerPerformance[];
  echoMakers: number;
  referralEchoes: number;
  echoConversionRate: number;
  ratedEchoes: number;
  averageRating: number | null;
};

// Totals derive from the leaderboard rows so the KPI tiles and the table can
// never disagree. Counts are coerced defensively: PostgREST serializes the
// view's bigint aggregates as numbers, but a string would silently
// concatenate instead of add.
//
// Web redemptions come from the leaderboard's web_redemptions when the view
// serves it; otherwise they are counted from the redemption feed rows.
export function sharingTotals(
  referrers: readonly SharingReferrerRow[],
  redemptions: readonly Pick<SharingRedemptionRow, "channel">[] = [],
): SharingTotals {
  let totalRedemptions = 0;
  let newUsers = 0;
  let activeReferrers = 0;
  let creditsEarned = 0;
  let creditsPending = 0;
  let leaderboardWebRedemptions: number | null = null;
  for (const row of referrers) {
    const redemptions = Number(row.redemptions) || 0;
    totalRedemptions += redemptions;
    newUsers += Number(row.new_users) || 0;
    creditsEarned += Number(row.credits_earned) || 0;
    creditsPending += Number(row.credits_pending) || 0;
    if (redemptions > 0) activeReferrers += 1;
    if (row.web_redemptions != null) {
      leaderboardWebRedemptions =
        (leaderboardWebRedemptions ?? 0) + (Number(row.web_redemptions) || 0);
    }
  }
  return {
    codesIssued: referrers.length,
    totalRedemptions,
    newUsers,
    activeReferrers,
    creditsEarned,
    creditsPending,
    webRedemptions:
      leaderboardWebRedemptions ??
      redemptions.filter((row) => redemptionChannel(row) === "web").length,
  };
}

/**
 * Join the Supabase referral ledger to the live Echo projection in memory.
 *
 * The two systems intentionally keep separate ownership: Supabase records the
 * inviter -> friend relationship, while the AWS dashboard is authoritative for
 * Echo outcomes. Joining on the normalized WhatsApp id keeps reporting current
 * without copying pipeline state into the referral ledger.
 */
export function referralAnalytics(
  referrers: readonly SharingReferrerRow[],
  redemptions: readonly SharingRedemptionRow[],
  echoes: readonly EchoflicksVideoSession[],
): ReferralAnalytics {
  const echoesByUser = new Map<string, EchoflicksVideoSession[]>();
  for (const echo of echoes) {
    const waId = normalizedWhatsAppId(echo.whatsapp_user_id);
    if (!waId) continue;
    const current = echoesByUser.get(waId) ?? [];
    current.push(echo);
    echoesByUser.set(waId, current);
  }
  for (const userEchoes of echoesByUser.values()) {
    userEchoes.sort((a, b) => echoTimestamp(b) - echoTimestamp(a));
  }

  const relationships = redemptions.map<ReferralRelationship>((redemption) => {
    const waId = normalizedWhatsAppId(redemption.referred_wa_id);
    const userEchoes = waId ? (echoesByUser.get(waId) ?? []) : [];
    return {
      ...redemption,
      echoes: userEchoes,
      latestEcho: userEchoes[0] ?? null,
    };
  });

  const relationshipsByReferrer = new Map<string, ReferralRelationship[]>();
  for (const relationship of relationships) {
    const waId = normalizedWhatsAppId(relationship.referrer_wa_id);
    if (!waId) continue;
    const current = relationshipsByReferrer.get(waId) ?? [];
    current.push(relationship);
    relationshipsByReferrer.set(waId, current);
  }

  const referrerPerformance = referrers.map<ReferrerPerformance>((referrer) => {
    const waId = normalizedWhatsAppId(referrer.owner_wa_id);
    const invited = waId ? (relationshipsByReferrer.get(waId) ?? []) : [];
    const echoMakers = invited.filter((row) => row.echoes.length > 0).length;
    const referralEchoes = invited.reduce(
      (total, row) => total + row.echoes.length,
      0,
    );
    return {
      ...referrer,
      echoMakers,
      referralEchoes,
      echoConversionRate: invited.length ? echoMakers / invited.length : 0,
    };
  });

  const referralEchoes = relationships.flatMap((row) => row.echoes);
  const ratings = referralEchoes
    .map((echo) => Number(echo.rating))
    .filter((rating) => Number.isFinite(rating) && rating >= 1 && rating <= 5);
  const echoMakers = relationships.filter(
    (row) => row.echoes.length > 0,
  ).length;

  return {
    relationships,
    referrers: referrerPerformance,
    echoMakers,
    referralEchoes: referralEchoes.length,
    echoConversionRate: relationships.length
      ? echoMakers / relationships.length
      : 0,
    ratedEchoes: ratings.length,
    averageRating: ratings.length
      ? ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length
      : null,
  };
}

function normalizedWhatsAppId(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const digits = value.replace(/[^0-9]/g, "");
  return digits.length >= 6 ? digits : null;
}

function echoTimestamp(echo: EchoflicksVideoSession): number {
  const value = echo.completed_at ?? echo.display_date ?? echo.started_at;
  if (!value) return 0;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
}

// A row's display label: the resolved CRM contact name when the phone join
// matched, otherwise the raw WhatsApp id.
export function sharingDisplayName(
  name: string | null | undefined,
  waId: string,
): string {
  return (name ?? "").trim() || waId;
}
